import { FC, useCallback, useMemo, useState } from "react";

import { CountryTradeObject } from "../../../Charts/shared/chart.type";
import { Alert } from "../../../Alert/Alert";
import { useAsyncData } from "../../../../hooks/useAsyncData";

import { Bubble } from "../../../Charts/BubbleChart/BubbleChart.type";
import { BubbleChart } from "../../../Charts/BubbleChart/BubbleChart";
import { FilterDropdown } from "../shared/filter/FilterDropdown";
import { FilterInput } from "../shared/filter/FilterDropdown.type";

import { LABEL_COLOR } from "./Top10ImportAndExportCountries.const";
import { getUnit } from "./Top10ImportAndExportCountries.helper";
import { Top10ImportAndExportCountriesProps } from "./Top10ImportAndExportCountries.type";

const Top10ImportAndExportCountries: FC<Top10ImportAndExportCountriesProps> = ({ data }: { data: () => Promise<CountryTradeObject[]> }) => {
	const [type, setType] = useState<"Import" | "Export">("Export");
	const [category, setCategory] = useState<string>("Total");
	const [unit, setUnit] = useState<"valueInEuros" | "quantityInKg">("valueInEuros");

	const { data: fetchedData, loading, error } = useAsyncData<CountryTradeObject[]>(data);

	// Left dataset
	const leftBubblechartDataset = useMemo(() => {
		if (!fetchedData || !type || !category || !unit) {
			return;
		}
		const rawData2014 = fetchedData.filter((tradeObject) => tradeObject.period === "2014");
		if (!rawData2014 || rawData2014.length < 0) {
			return;
		}
		const filteredData = rawData2014.filter((tradeObject) => tradeObject.flowName === type && tradeObject.familyName === category);
		return filteredData
			.map((tradeObject) => {
				return { label: tradeObject.countryReporter.toString(), value: tradeObject[unit] };
			})
			.sort((a, b) => b.value - a.value)
			.slice(0, 10) as Bubble[];
	}, [category, fetchedData, type, unit]);

	// Right dataset
	const rightBubblechartDataset = useMemo(() => {
		if (!fetchedData) {
			return;
		}
		const rawData2025 = fetchedData.filter((tradeObject) => tradeObject.period === "2025");
		if (!rawData2025 || rawData2025.length < 0) {
			return;
		}
		const filteredData = rawData2025.filter((tradeObject) => tradeObject.flowName === type && tradeObject.familyName === category);
		return filteredData
			.map((tradeObject) => {
				return { label: tradeObject.countryReporter.toString(), value: tradeObject[unit] };
			})
			.sort((a, b) => b.value - a.value)
			.slice(0, 10) as Bubble[];
	}, [category, fetchedData, type, unit]);

	const maxValue = useMemo(() => {
		if (!leftBubblechartDataset || !rightBubblechartDataset) {
			return;
		}
		const leftMax = Math.max(...leftBubblechartDataset.map((bubble) => bubble.value));
		const rightMax = Math.max(...rightBubblechartDataset.map((bubble) => bubble.value));
		return leftMax >= rightMax ? leftMax : rightMax;
	}, [leftBubblechartDataset, rightBubblechartDataset]);

	const numberMillionFormat = useCallback(
		(value: number) => {
			const milionRoundUp = Math.ceil(value / 1000000);
			const unitChar = unit === "valueInEuros" ? "€" : "Kg";
			return `${milionRoundUp.toLocaleString("nl-BE")} M${unitChar}`;
		},
		[unit],
	);

	const handleDropdownChange = useCallback((value: FilterInput) => {
		const { type: selectedType, category: selectedCategory, unit: selectedUnit } = value;
		setType(selectedType as "Import" | "Export");
		setCategory(selectedCategory);
		setUnit(`${getUnit(selectedUnit)}`);
	}, []);

	return (
		<>
			{loading && <p>Loading data...</p>}

			{error && <Alert type="error" message={error} />}

			{!loading && !error && (
				<>
					<div>
						<div className="mb-10">
							<h3 className="flex gap-2 mb-10">Top 10 import and export countries</h3>
							<FilterDropdown onChange={handleDropdownChange} />
						</div>
						<div className="flex sm:flex-row flex-col gap-60 lg:ml-5 ml-10">
							<div className="sm:w-1/6 w-3/4 sm:ml-0 ml-5 pr-10 sm:pr-0">
								{leftBubblechartDataset && (
									<BubbleChart
										data={leftBubblechartDataset}
										max={maxValue}
										height={750}
										margin={{ top: 0, left: 0, right: 0, bottom: 0 }}
										YAxisProps={{ strokeWidth: "2px", tickLine: { stroke: "#b2b2b2" }, dx: -5 }}
										XAxisProps={{
											label: {
												value: "2014",
												position: "insideRight",
												transform: "translate(25, -20)",
												fontWeight: 800,
												fill: LABEL_COLOR,
											},
											orientation: "top",
										}}
										labelFormatter={numberMillionFormat}
									/>
								)}
							</div>
							<div className="sm:w-1/6 w-3/4 sm:ml-0 ml-5">
								{rightBubblechartDataset && (
									<BubbleChart
										data={rightBubblechartDataset}
										max={maxValue}
										height={750}
										margin={{ top: 0, left: 0, right: 0, bottom: 0 }}
										YAxisProps={{ strokeWidth: "2px", tickLine: { stroke: "#b2b2b2" }, dx: -5 }}
										XAxisProps={{
											label: {
												value: "2025",
												position: "insideRight",
												transform: "translate(25, -20)",
												fontWeight: 800,
												fill: LABEL_COLOR,
											},
											orientation: "top",
										}}
										labelFormatter={numberMillionFormat}
									/>
								)}
							</div>
						</div>
					</div>
				</>
			)}
		</>
	);
};

export { Top10ImportAndExportCountries };
